import axios from './api';
const API_URL = "/user";

const empresaOid = (item) =>
  item?.OID_EMPRESA ?? item?.oid_empresa ?? item?.oidEmpresa ?? null;

/**
 * Login stores `empresa` as the list of condomínios (EMPRESAS.OID_USUARIO).
 * Older sessions, a single-row lookup, or a paginated `{ data: [] }` envelope
 * must all collapse to the full array of empresas.
 */
const normalizeEmpresas = (empresa) => {
  if (Array.isArray(empresa)) {
    return empresa.filter((item) => item && empresaOid(item) != null);
  }
  if (empresa && typeof empresa === "object") {
    if (empresaOid(empresa) != null) {
      return [empresa];
    }
    if (Array.isArray(empresa.data)) {
      return normalizeEmpresas(empresa.data);
    }
    if (Array.isArray(empresa.rows)) {
      return normalizeEmpresas(empresa.rows);
    }
    if (Array.isArray(empresa.empresas)) {
      return normalizeEmpresas(empresa.empresas);
    }
    const values = Object.values(empresa);
    if (
      values.length > 0 &&
      values.every((item) => item && typeof item === "object" && empresaOid(item) != null)
    ) {
      return values;
    }
  }
  return [];
};

const getCurrentUser = () => {
  try {
    const session = JSON.parse(localStorage.getItem("user"));
    if (session) {
      session.empresa = normalizeEmpresas(session.empresa);
    }
    return session;
  } catch {
    return null;
  }
};

const getLinkedEmpresas = (session) =>
  normalizeEmpresas((session || getCurrentUser())?.empresa);

const getUserStatus = (session) => {
  const raw = (session || getCurrentUser())?.user?.DS_STATUS;
  if (raw === null || raw === undefined || raw === "") return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
};

const isAdmin = (session) => getUserStatus(session) === 10;
const isSindico = (session) => getUserStatus(session) === 1;
const isMorador = (session) => getUserStatus(session) === 2;

const persistSession = (session) => {
  const next = session
    ? { ...session, empresa: normalizeEmpresas(session.empresa) }
    : session;
  localStorage.setItem("user", JSON.stringify(next));
  window.dispatchEvent(
    new CustomEvent("syskeeper:session-updated", { detail: next }),
  );
  return next;
};

/**
 * Atualiza a lista de condomínios do usuário logado a partir da API.
 * Evita precisar de logout/login após o admin vincular EMPRESAS.OID_USUARIO.
 */
const refreshEmpresas = async () => {
  const session = getCurrentUser();
  const oidUsuario = Number(session?.user?.OID_USUARIO);
  if (!session?.token || !Number.isFinite(oidUsuario) || oidUsuario <= 0) {
    return session;
  }

  try {
    const { data } = await axios.get(`/empresa/usuario/${oidUsuario}`, {
      params: { limit: 10000, page: 1 },
    });
    const list = normalizeEmpresas(data);
    const next = { ...session, empresa: list };
    return persistSession(next);
  } catch (err) {
    console.warn("[Auth] falha ao atualizar condomínios do usuário", err);
    return session;
  }
};

const signup = (email, password) => {
  return axios
    .post(API_URL + "/user", {
      email,
      password,
    })
    .then((response) => {
      if (response.data.accessToken) {
        localStorage.setItem("user", JSON.stringify(response.data));
      }

      return response.data;
    });
};

const login = (DS_EMAIL, CLIENT_SECRET) => {
  return axios
    .post(API_URL + "/login", {
        DS_EMAIL,
        CLIENT_SECRET,
    })
    .then(async (response) => {
      if (response.data.token) {
        persistSession({
          ...response.data,
          empresa: normalizeEmpresas(response.data.empresa),
        });
        if (axios?.defaults?.headers?.common) {
          axios.defaults.headers.common.Authorization = `Bearer ${response.data.token}`;
        }
        await refreshEmpresas();
      }

      return getCurrentUser() || response.data;
    });
};

const logout = () => {
  localStorage.removeItem("user");
  if (axios?.defaults?.headers?.common) {
    delete axios.defaults.headers.common.Authorization;
  }
};

const authService = {
  signup,
  login,
  logout,
  getCurrentUser,
  getLinkedEmpresas,
  getUserStatus,
  isAdmin,
  isSindico,
  isMorador,
  normalizeEmpresas,
  refreshEmpresas,
  persistSession,
};

export default authService;
